/**
 * 1 Nov 2026 (D-1102-CREW-3): "Crew can save while offline" — the queue that keeps the crew's Arrived and Finish (with
 * their photos) on the phone when there is no signal, and sends them, in order, when it returns.
 *
 * Nothing here knows about React. The rules, each with a test:
 *   - an action keeps the SAME form session (idempotency key) the live press would have used, so a send that is made
 *     twice — a lost answer, two tabs, a retry — can never double a visit, a close-out or a photo;
 *   - order is kept per visit: Arrived is sent before Finish, and a step is not sent while an earlier step of the same
 *     visit is waiting or was refused;
 *   - a refusal from the server stays in the list with the server's words ("Couldn't send — tell the office"); it is
 *     never dropped and never retried by itself (the person presses Try again);
 *   - only a network failure (or the server being busy) is retried by itself, with a back-off that grows to five
 *     minutes; a busy server gives up into a refusal after MAX_BUSY tries;
 *   - photos are held in the phone's storage up to MAX_QUEUE_BYTES; past that nothing more is kept and the person is told.
 */
import { ApiRequestError } from '@/lib/api';

export type QueuedKind = 'arrived' | 'finish';
export type PhotoTarget = 'photos' | 'form.photos' | 'form.unitCondition.photos';

export interface QueuedPhoto {
  target: PhotoTarget;
  purpose: 'before' | 'door' | 'after' | 'unit-condition';
  name: string;
  /** Present until the photo has been taken by the server. */
  blob?: Blob;
  /** The visit folder path once the server has the photo (or when it was sent before the signal went). */
  path?: string;
  bytes: number;
}

export interface QueuedAction {
  /** Derived from the form session's idempotency key: the same press queued twice is one action. */
  id: string;
  user: string;
  kind: QueuedKind;
  appointmentId: string;
  unit: string;
  day: string;
  /** What the tracker calls it once sent ("Arrived · 703"). */
  label: string;
  seq: number;
  createdAt: number;
  status: 'waiting' | 'refused';
  /** The server's own words, when refused. */
  message?: string;
  attempts: number;
  nextTryAt: number;
  endpoint: string;
  /** The request exactly as the live press would send it, `session` (the idempotency key) included. */
  body: Record<string, unknown>;
  photos: QueuedPhoto[];
}

/** The `message` of a step that was not sent because an earlier step of the same visit was refused (the screen says it in words). */
export const EARLIER_REFUSED = 'earlier-step-refused';
export const MAX_QUEUE_BYTES = 40 * 1024 * 1024;
export const MAX_BUSY = 6;
export const BACKOFF_FIRST_MS = 10_000;
export const BACKOFF_MAX_MS = 5 * 60_000;

export interface QueueStore {
  all(): Promise<QueuedAction[]>;
  put(a: QueuedAction): Promise<void>;
  remove(id: string): Promise<void>;
}

/** A store that lives for this page only — tests, and a phone whose storage is closed to the app. */
export function memoryStore(): QueueStore & { persistent: false } {
  const m = new Map<string, QueuedAction>();
  return {
    persistent: false,
    all: async () => [...m.values()],
    put: async (a) => void m.set(a.id, a),
    remove: async (id) => void m.delete(id),
  };
}

const DB = 'abf-crew-queue';
const STORE = 'actions';

/** IndexedDB, or null where the phone does not give the app one (then nothing is kept and the person is told). */
export function indexedDbStore(): (QueueStore & { persistent: true }) | null {
  if (typeof indexedDB === 'undefined') return null;
  let dbp: Promise<IDBDatabase> | null = null;
  const open = () =>
    (dbp ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        dbp = null;
        reject(req.error ?? new Error('IndexedDB would not open'));
      };
    }));
  const run = async <T,>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const r = f(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(r.result);
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB was stopped'));
    });
  };
  return {
    persistent: true,
    all: () => run<QueuedAction[]>('readonly', (s) => s.getAll() as IDBRequest<QueuedAction[]>),
    put: async (a) => void (await run('readwrite', (s) => s.put(a))),
    remove: async (id) => void (await run('readwrite', (s) => s.delete(id))),
  };
}

/* ---------------- what a failure means ---------------- */

export type Outcome =
  | { kind: 'sent'; message?: string }
  /** No signal / the request never got an answer: wait and try again. */
  | { kind: 'network' }
  /** Signed out: nothing is lost, it waits until the person signs in again. */
  | { kind: 'auth' }
  /** The server was busy or failed: try again, a few times. */
  | { kind: 'busy'; message: string }
  /** The server said no: it stays listed, with its words. */
  | { kind: 'refused'; message: string };

const BUSY_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

export function classify(e: unknown, ctx: { user: string }): Outcome {
  if (e instanceof ApiRequestError) {
    const body = (e.body ?? {}) as { error?: string; message?: string; by?: string };
    if (e.status === 401 || body.error === 'not-json') return { kind: 'auth' };
    // the same press, already taken (the answer to a send whose reply was lost): that is what was wanted
    if (body.error === 'already' && body.by && body.by.trim().toLowerCase() === ctx.user.trim().toLowerCase()) return { kind: 'sent', message: body.message };
    if (BUSY_STATUS.has(e.status)) return { kind: 'busy', message: body.message ?? e.message };
    return { kind: 'refused', message: body.message ?? e.message };
  }
  // fetch() itself failed: no signal, a dropped connection, a timeout
  return { kind: 'network' };
}

/** True when the failure is "no signal" rather than an answer — the live press then keeps the work on the phone. */
export function isNetworkFailure(e: unknown): boolean {
  if (e instanceof ApiRequestError) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return e instanceof TypeError || (e instanceof Error && /failed to fetch|load failed|network ?error|network request failed|aborted/i.test(e.message));
}

/* ---------------- the engine ---------------- */

export interface QueueDeps {
  store: QueueStore;
  /** The request, as apiFetch would send it (throws ApiRequestError for an answer, anything else for no answer). */
  post: (endpoint: string, body: unknown) => Promise<unknown>;
  /** One photo to the visit's folder; the same `key` always answers the same path. Answers the path. */
  upload: (a: QueuedAction, p: QueuedPhoto, key: string) => Promise<string>;
  now: () => number;
  online: () => boolean;
  /** An action was taken by the server (`res` is its answer). */
  sent: (a: QueuedAction, res: unknown, message?: string) => void;
  /** The list changed. */
  changed: () => void;
}

export function backoffMs(attempts: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_FIRST_MS * 2 ** Math.max(0, attempts - 1));
}

/** The request body with the photos' paths put where the live press puts them. */
export function bodyOf(a: QueuedAction): Record<string, unknown> {
  const body = structuredCloneJson(a.body);
  for (const target of ['photos', 'form.photos', 'form.unitCondition.photos'] as const) {
    const mine = a.photos.filter((p) => p.target === target);
    if (!mine.length || !hasPath(body, target)) continue;
    setPath(body, target, mine.map((p) => p.path).filter((x): x is string => !!x));
  }
  return body;
}

const structuredCloneJson = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

function hasPath(o: Record<string, unknown>, path: string): boolean {
  let cur: unknown = o;
  for (const k of path.split('.')) {
    if (!cur || typeof cur !== 'object' || !(k in (cur as object))) return false;
    cur = (cur as Record<string, unknown>)[k];
  }
  return true;
}
function setPath(o: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let cur: Record<string, unknown> = o;
  for (const k of keys.slice(0, -1)) {
    const next = cur[k];
    if (!next || typeof next !== 'object') return; // e.g. no unit condition on this close-out: nothing to put the photos in
    cur = next as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]!] = value;
}

export class OfflineQueue {
  private items: QueuedAction[] = [];
  private running: Promise<void> | null = null;
  private counter = 0;
  /** Set when a storage write failed: what is on the screen may not survive a reload. */
  storageFailed = false;

  constructor(private deps: QueueDeps) {}

  list(user?: string): QueuedAction[] {
    return this.items.filter((a) => !user || a.user === user).sort((a, b) => a.seq - b.seq);
  }

  async load(): Promise<void> {
    try {
      const all = await this.deps.store.all();
      // keep anything made while loading
      const have = new Set(this.items.map((a) => a.id));
      this.items = [...this.items, ...all.filter((a) => !have.has(a.id))];
    } catch {
      this.storageFailed = true;
    }
    this.deps.changed();
  }

  bytes(user?: string): number {
    return this.list(user).reduce((n, a) => n + a.photos.reduce((m, p) => m + (p.blob && !p.path ? p.bytes : 0), 0), 0);
  }

  /**
   * Keep an action on the phone. `full` when the photos would go past MAX_QUEUE_BYTES (nothing is kept — the person
   * is told); `storage` when the phone's storage would not take it.
   */
  async enqueue(input: Omit<QueuedAction, 'seq' | 'createdAt' | 'status' | 'attempts' | 'nextTryAt'>): Promise<'saved' | 'full' | 'storage'> {
    const existing = this.items.find((a) => a.id === input.id);
    const add = input.photos.reduce((n, p) => n + (p.blob && !p.path ? p.bytes : 0), 0);
    const already = existing ? existing.photos.reduce((n, p) => n + (p.blob && !p.path ? p.bytes : 0), 0) : 0;
    if (this.bytes() - already + add > MAX_QUEUE_BYTES) return 'full';
    const now = this.deps.now();
    const a: QueuedAction = existing
      ? { ...existing, ...input, seq: existing.seq, createdAt: existing.createdAt, status: 'waiting', attempts: 0, nextTryAt: 0, message: undefined }
      : { ...input, seq: now * 1000 + (this.counter++ % 1000), createdAt: now, status: 'waiting', attempts: 0, nextTryAt: 0 };
    try {
      await this.deps.store.put(a);
    } catch {
      this.storageFailed = true;
      return 'storage';
    }
    this.items = [...this.items.filter((x) => x.id !== a.id), a];
    this.deps.changed();
    return 'saved';
  }

  /** Waiting items for a visit (what the card should treat as already done on this phone). */
  waitingFor(appointmentId: string, kind?: QueuedKind): QueuedAction[] {
    return this.items.filter((a) => a.appointmentId === appointmentId && (!kind || a.kind === kind) && a.status === 'waiting');
  }
  heldFor(appointmentId: string): QueuedAction[] {
    return this.items.filter((a) => a.appointmentId === appointmentId);
  }

  /** The soonest time something is due to be tried again (for the timer); null: nothing to wait for. */
  nextDue(user: string): number | null {
    const waiting = this.list(user).filter((a) => a.status === 'waiting');
    return waiting.length ? Math.min(...waiting.map((a) => a.nextTryAt)) : null;
  }

  /** Try a refused action again (the person pressed Try again). */
  async retry(id: string): Promise<void> {
    const a = this.items.find((x) => x.id === id);
    if (!a || a.status !== 'refused') return;
    await this.save({ ...a, status: 'waiting', message: undefined, attempts: 0, nextTryAt: 0 });
    this.deps.changed();
  }

  /** The person removed it from this phone (they were told what that means). */
  async remove(id: string): Promise<void> {
    try {
      await this.deps.store.remove(id);
    } catch {
      this.storageFailed = true;
    }
    this.items = this.items.filter((x) => x.id !== id);
    this.deps.changed();
  }

  private async save(a: QueuedAction): Promise<void> {
    this.items = this.items.map((x) => (x.id === a.id ? a : x));
    try {
      await this.deps.store.put(a);
    } catch {
      this.storageFailed = true;
    }
  }

  /** Send what is waiting for `user`, in order. One run at a time; a second call waits for the one running. */
  flush(user: string, force = false): Promise<void> {
    if (this.running) return this.running;
    this.running = this.run(user, force).finally(() => {
      this.running = null;
      this.deps.changed();
    });
    return this.running;
  }

  private async run(user: string, force: boolean): Promise<void> {
    // per visit: a step is not sent while an earlier step of the visit is waiting for its time or was refused
    const held = new Set<string>();
    for (const a0 of this.list(user)) {
      // read again: an earlier step may have changed what this one needs
      const a = this.items.find((x) => x.id === a0.id);
      if (!a) continue;
      if (a.status === 'refused') {
        held.add(a.appointmentId);
        continue;
      }
      if (held.has(a.appointmentId)) {
        // an earlier step of this visit was refused: this one cannot go before it — it says so rather than waiting unseen
        if (this.items.some((x) => x.appointmentId === a.appointmentId && x.status === 'refused' && x.seq < a.seq)) {
          await this.save({ ...a, status: 'refused', message: EARLIER_REFUSED });
          this.deps.changed();
        }
        continue;
      }
      if (!this.deps.online()) return;
      if (!force && a.nextTryAt > this.deps.now()) {
        held.add(a.appointmentId);
        continue;
      }
      const out = await this.attempt(a);
      if (out.kind === 'network' || out.kind === 'auth') return; // nothing else will get through either
      if (out.kind !== 'sent') held.add(a.appointmentId);
    }
  }

  private async attempt(a: QueuedAction): Promise<Outcome> {
    let cur = a;
    try {
      // 1. the photos still on the phone, one by one (progress kept after each)
      for (let i = 0; i < cur.photos.length; i++) {
        const p = cur.photos[i]!;
        if (p.path || !p.blob) continue;
        const path = await this.deps.upload(cur, p, `${cur.id}-p${i}`);
        cur = { ...cur, photos: cur.photos.map((x, j) => (j === i ? { ...x, path, blob: undefined } : x)) };
        await this.save(cur);
      }
      // 2. the press itself — the same session it would have carried live
      const res = await this.deps.post(cur.endpoint, bodyOf(cur));
      try {
        await this.deps.store.remove(cur.id);
      } catch {
        this.storageFailed = true;
      }
      this.items = this.items.filter((x) => x.id !== cur.id);
      this.deps.sent(cur, res);
      return { kind: 'sent' };
    } catch (e) {
      const out = classify(e, { user: cur.user });
      if (out.kind === 'sent') {
        try {
          await this.deps.store.remove(cur.id);
        } catch {
          this.storageFailed = true;
        }
        this.items = this.items.filter((x) => x.id !== cur.id);
        this.deps.sent(cur, null, out.message);
        return out;
      }
      if (out.kind === 'refused') await this.save({ ...cur, status: 'refused', message: out.message });
      else if (out.kind === 'busy') {
        const attempts = cur.attempts + 1;
        if (attempts >= MAX_BUSY) await this.save({ ...cur, status: 'refused', attempts, message: `${out.message} (tried ${attempts} times)` });
        else await this.save({ ...cur, attempts, nextTryAt: this.deps.now() + backoffMs(attempts) });
      } else if (out.kind === 'auth') await this.save({ ...cur, nextTryAt: this.deps.now() + 60_000 });
      else if (out.kind === 'network') await this.save({ ...cur, attempts: cur.attempts + 1, nextTryAt: this.deps.now() + backoffMs(cur.attempts + 1) });
      return out;
    } finally {
      this.deps.changed();
    }
  }
}
